import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import {
  Storefront, MapPin, Phone, Envelope, ArrowSquareOut, Clock,
  ArrowLeft, PencilSimple, Trash, Plus, X, Buildings, MagnifyingGlass, ImageSquare,
} from "@phosphor-icons/react";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import UserLink from "../components/UserLink";
import {
  Header, ScopeTabs, Loading, Empty, NotFound,
  FormField, FormModal, inputCls,
} from "./MeetupsPage";

const CATEGORIES = [
  ["all","All"],["food","Food"],["retail","Retail"],["service","Service"],["wellness","Wellness"],
  ["creative","Creative"],["professional","Professional"],["tourism","Tourism"],["nonprofit","Nonprofit"],["other","Other"],
];

/**
 * BusinessesPage — discoverable local business profiles.
 * /app/businesses            → directory + search + create
 * /app/businesses/:bid       → detail page with banner, logo, contacts, hours
 */
export default function BusinessesPage() {
  const { bid } = useParams();
  return bid ? <BusinessDetail bid={bid}/> : <BusinessesList/>;
}


function BusinessesList() {
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("all");
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = { category };
      if (scope === "mine") params.mine = true;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/businesses", { params });
      setItems(data || []);
    } catch { setItems([]); }
    setLoading(false);
  };
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [scope, category, q]);

  const create = async (payload) => {
    const { data } = await api.post("/businesses", payload);
    setCreateOpen(false);
    navigate(`/app/businesses/${data.business_id}`);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="businesses-list">
      <Header title="Businesses" sub="Command Center · Businesses"
              actionLabel="New Business" onAction={() => setCreateOpen(true)}
              testid="businesses"/>

      <div className="flex items-center gap-3 flex-wrap">
        <ScopeTabs scope={scope} onChange={setScope}
                   options={[["all","All"],["mine","Mine"]]} testid="businesses-scope"/>
        <div className="flex-1 min-w-[220px] max-w-md rounded-lg flex items-center gap-2 px-3"
             style={{ border: "1px solid rgba(212,175,55,0.4)", background: "rgba(0,0,0,0.4)" }}>
          <MagnifyingGlass size={13} className="text-[#FFD700]"/>
          <input data-testid="businesses-search" value={q}
                 onChange={(e) => setQ(e.target.value)}
                 placeholder="Search businesses"
                 className="flex-1 bg-transparent py-2 text-[13px] text-[#F2DC8C] placeholder:text-[#F2DC8C]/40 focus:outline-none"/>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        {CATEGORIES.map(([k, l]) => (
          <button key={k} onClick={() => setCategory(k)}
                  data-testid={`businesses-cat-${k}`}
                  className="h-8 px-3 rounded-full text-[10px] uppercase tracking-widest"
                  style={category === k
                    ? { background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }
                    : { border: "1px solid rgba(212,175,55,0.4)", color: "#F2DC8C" }}>
            {l}
          </button>
        ))}
      </div>

      {loading ? <Loading/>
        : items.length === 0 ? <Empty label="No businesses here yet." onCreate={() => setCreateOpen(true)} cta="Add Your Business" icon={Storefront}/>
        : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="businesses-grid">
            {items.map((b) => <BusinessTile key={b.business_id} biz={b}/>)}
          </div>
        )}

      <AnimatePresence>
        {createOpen && <BusinessModal onClose={() => setCreateOpen(false)} onSave={create}/>}
      </AnimatePresence>
    </div>
  );
}


function BusinessTile({ biz }) {
  return (
    <Link to={`/app/businesses/${biz.business_id}`} data-testid={`biz-tile-${biz.business_id}`}
          className="block rounded-2xl overflow-hidden transition-all hover:-translate-y-0.5"
          style={{
            background: "linear-gradient(180deg, rgba(26,18,6,0.75) 0%, rgba(5,3,0,0.9) 100%)",
            border: "1px solid rgba(212,175,55,0.45)",
          }}>
      <div className="h-24 w-full relative overflow-hidden"
           style={{ background: "linear-gradient(120deg, rgba(255,215,0,0.15), rgba(212,175,55,0.05))" }}>
        {biz.banner_image && (
          <img src={`${API}/files/${biz.banner_image}`} alt="" className="w-full h-full object-cover"/>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 overflow-hidden -mt-8 border-2"
                style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)",
                         borderColor: "#0a0703", boxShadow: "0 4px 12px rgba(0,0,0,0.5)" }}>
            {biz.logo_image ? (
              <img src={`${API}/files/${biz.logo_image}`} alt="" className="w-full h-full object-cover"/>
            ) : (
              <Storefront size={16} weight="fill" color="#0a0703"/>
            )}
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-display text-[16px] text-[#FFD700] truncate">{biz.name}</div>
            {biz.tagline && <div className="text-[11px] text-[#F2DC8C]/70 truncate">{biz.tagline}</div>}
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#F2DC8C]/60">
          <span className="px-1.5 py-0.5 rounded" style={{ border: "1px solid rgba(212,175,55,0.4)", color: "#FFD700" }}>
            {biz.category}
          </span>
          {biz.location && <span className="truncate inline-flex items-center gap-1"><MapPin size={10}/> {biz.location}</span>}
        </div>
      </div>
    </Link>
  );
}


function BusinessDetail({ bid }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/businesses/${bid}`); setB(data); } catch { setB(null); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bid]);

  const isOwner = b && user && b.owner_id === user.user_id;
  const remove = async () => {
    if (!window.confirm("Delete this business?")) return;
    await api.delete(`/businesses/${bid}`);
    navigate("/app/businesses");
  };
  const save = async (payload) => {
    await api.put(`/businesses/${bid}`, payload); setEditOpen(false); load();
  };

  if (loading) return <Loading/>;
  if (!b) return <NotFound label="Business not found" backTo="/app/businesses"/>;

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="business-detail">
      <Link to="/app/businesses" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#F2DC8C]/70 hover:text-[#FFD700]">
        <ArrowLeft size={12}/> All Businesses
      </Link>
      <div className="rounded-2xl overflow-hidden"
           style={{ background: "linear-gradient(180deg, rgba(26,18,6,0.85) 0%, rgba(5,3,0,0.95) 100%)",
                    border: "1px solid rgba(255,215,0,0.4)",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.55)" }}>
        <div className="h-40 w-full relative overflow-hidden"
             style={{ background: "linear-gradient(120deg, rgba(255,215,0,0.15), rgba(212,175,55,0.05))" }}>
          {b.banner_image && (
            <img src={`${API}/files/${b.banner_image}`} alt="" className="w-full h-full object-cover"/>
          )}
        </div>
        <div className="px-6 pb-6 -mt-10">
          <div className="flex items-end gap-4">
            <span className="w-20 h-20 rounded-2xl flex items-center justify-center shrink-0 overflow-hidden border-4"
                  style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)",
                           borderColor: "#0a0703", boxShadow: "0 8px 24px rgba(0,0,0,0.55)" }}>
              {b.logo_image ? (
                <img src={`${API}/files/${b.logo_image}`} alt="" className="w-full h-full object-cover"/>
              ) : <Storefront size={28} weight="fill" color="#0a0703"/>}
            </span>
            <div className="flex-1 min-w-0 pb-1">
              <h1 className="font-display text-[26px] leading-tight"
                  style={{
                    background: "linear-gradient(180deg, #FFF3AE 0%, #FFD700 45%, #8a6a00 100%)",
                    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                  }}>
                {b.name}
              </h1>
              {b.tagline && <div className="text-[13px] text-[#F2DC8C]/80">{b.tagline}</div>}
              <div className="mt-1 text-[10px] uppercase tracking-widest text-[#F2DC8C]/55">
                <span className="text-[#FFD700]">{b.category}</span> · <UserLink username={b.owner_username}>@{b.owner_username}</UserLink>
              </div>
            </div>
          </div>

          {b.description && (
            <p className="mt-5 text-[13px] text-[#F2DC8C] leading-relaxed whitespace-pre-wrap">{b.description}</p>
          )}

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12px]">
            {b.location && <InfoRow icon={MapPin} label="Location" value={b.location}/>}
            {b.hours    && <InfoRow icon={Clock}  label="Hours"    value={b.hours}/>}
            {b.phone    && <InfoRow icon={Phone}  label="Phone"    value={b.phone} href={`tel:${b.phone}`}/>}
            {b.email    && <InfoRow icon={Envelope} label="Email"  value={b.email} href={`mailto:${b.email}`}/>}
            {b.website  && <InfoRow icon={ArrowSquareOut} label="Website" value={b.website} href={b.website} external/>}
          </div>

          {isOwner && (
            <div className="mt-6 flex items-center gap-2">
              <button onClick={() => setEditOpen(true)} data-testid="biz-edit"
                      className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl text-[11px] uppercase tracking-widest"
                      style={{ border: "1px solid rgba(212,175,55,0.55)", color: "#F2DC8C" }}>
                <PencilSimple size={11}/> Edit
              </button>
              <button onClick={remove} data-testid="biz-delete"
                      className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl text-[11px] uppercase tracking-widest text-[#FF5D73]"
                      style={{ border: "1px solid rgba(255,93,115,0.4)" }}>
                <Trash size={11}/> Delete
              </button>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {editOpen && <BusinessModal biz={b} onClose={() => setEditOpen(false)} onSave={save}/>}
      </AnimatePresence>
    </div>
  );
}


function InfoRow({ icon: Icon, label, value, href, external }) {
  const inner = (
    <>
      <Icon size={12} className="text-[#FFD700] shrink-0"/>
      <div className="min-w-0 flex-1">
        <div className="text-[9px] uppercase tracking-widest text-[#F2DC8C]/60">{label}</div>
        <div className="text-[12px] text-[#F2DC8C] truncate">{value}</div>
      </div>
    </>
  );
  const cls = "rounded-lg p-2 flex items-center gap-2";
  const style = { border: "1px solid rgba(212,175,55,0.35)", background: "rgba(0,0,0,0.35)" };
  if (href) {
    return (
      <a href={href} target={external ? "_blank" : undefined} rel={external ? "noopener noreferrer" : undefined}
         className={cls + " hover:bg-[rgba(255,215,0,0.05)] transition-colors"} style={style}>
        {inner}
      </a>
    );
  }
  return <div className={cls} style={style}>{inner}</div>;
}


function BusinessModal({ biz, onClose, onSave }) {
  const [name, setName] = useState(biz?.name || "");
  const [tagline, setTagline] = useState(biz?.tagline || "");
  const [description, setDescription] = useState(biz?.description || "");
  const [category, setCategory] = useState(biz?.category || "other");
  const [location, setLocation] = useState(biz?.location || "");
  const [website, setWebsite] = useState(biz?.website || "");
  const [phone, setPhone] = useState(biz?.phone || "");
  const [email, setEmail] = useState(biz?.email || "");
  const [hours, setHours] = useState(biz?.hours || "");
  const [banner, setBanner] = useState(biz?.banner_image || null);
  const [logo, setLogo] = useState(biz?.logo_image || null);
  const [busy, setBusy] = useState(false);

  const uploadFile = async (file, setter) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads/image", fd,
      { headers: { "Content-Type": "multipart/form-data" } });
    setter(data.file_id);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await onSave({
        name, tagline: tagline || null, description: description || null,
        category, location: location || null, website: website || null,
        phone: phone || null, email: email || null, hours: hours || null,
        banner_image: banner, logo_image: logo,
      });
    } finally { setBusy(false); }
  };

  return (
    <FormModal title={biz ? "Edit Business" : "New Business"} icon={Storefront}
               onClose={onClose} onSubmit={submit} busy={busy}
               disabled={!name.trim()}
               saveLabel={biz ? "Save" : "Create"} testid="biz-modal">
      <div className="grid grid-cols-2 gap-3">
        <ImagePicker label="Banner" fileId={banner} setter={(f) => uploadFile(f, setBanner)} onClear={() => setBanner(null)} testid="biz-banner"/>
        <ImagePicker label="Logo" fileId={logo} setter={(f) => uploadFile(f, setLogo)} onClear={() => setLogo(null)} testid="biz-logo"/>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Name">
          <input autoFocus data-testid="biz-name" value={name}
                 onChange={(e) => setName(e.target.value.slice(0, 140))}
                 placeholder="Aloha Coffee Co." className={inputCls}/>
        </FormField>
        <FormField label="Category">
          <select data-testid="biz-category" value={category}
                  onChange={(e) => setCategory(e.target.value)} className={inputCls}
                  style={{ colorScheme: "dark" }}>
            {CATEGORIES.filter(([k]) => k !== "all").map(([k, l]) => (
              <option key={k} value={k} style={{ background: "#0a0703" }}>{l}</option>
            ))}
          </select>
        </FormField>
      </div>
      <FormField label="Tagline">
        <input data-testid="biz-tagline" value={tagline}
               onChange={(e) => setTagline(e.target.value.slice(0, 200))}
               placeholder="Ethically sourced coffee, aloha included." className={inputCls}/>
      </FormField>
      <FormField label="Description">
        <textarea data-testid="biz-description" value={description} rows={4}
                  onChange={(e) => setDescription(e.target.value.slice(0, 8000))}
                  placeholder="Tell people what you do."
                  className={inputCls + " resize-none"}/>
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Location">
          <input data-testid="biz-location" value={location}
                 onChange={(e) => setLocation(e.target.value.slice(0, 300))}
                 placeholder="Lahaina, Maui" className={inputCls}/>
        </FormField>
        <FormField label="Website">
          <input data-testid="biz-website" value={website}
                 onChange={(e) => setWebsite(e.target.value.slice(0, 600))}
                 placeholder="https://…" className={inputCls}/>
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Phone">
          <input data-testid="biz-phone" value={phone}
                 onChange={(e) => setPhone(e.target.value.slice(0, 60))}
                 placeholder="808-555-1234" className={inputCls}/>
        </FormField>
        <FormField label="Email">
          <input data-testid="biz-email" value={email}
                 onChange={(e) => setEmail(e.target.value.slice(0, 200))}
                 placeholder="hi@example.com" className={inputCls}/>
        </FormField>
      </div>
      <FormField label="Hours (free-form)">
        <textarea data-testid="biz-hours" value={hours} rows={3}
                  onChange={(e) => setHours(e.target.value.slice(0, 1000))}
                  placeholder="Mon-Fri 7am-5pm · Sat 8am-3pm · Sun closed"
                  className={inputCls + " resize-none"}/>
      </FormField>
    </FormModal>
  );
}


function ImagePicker({ label, fileId, setter, onClear, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = "";
    setBusy(true);
    try { await setter(f); } catch (ex) { alert(ex?.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  return (
    <FormField label={label}>
      <div className="flex items-center gap-2">
        <div className="w-16 h-16 rounded-lg overflow-hidden flex items-center justify-center bg-black/40"
             style={{ border: "1px dashed rgba(212,175,55,0.5)" }}>
          {fileId ? (
            <img src={`${API}/files/${fileId}`} alt="" className="w-full h-full object-cover"/>
          ) : (
            <ImageSquare size={16} className="text-[#FFD700]"/>
          )}
        </div>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid={`${testid}-input`}/>
        <div className="flex flex-col gap-1">
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
                  data-testid={`${testid}-upload`}
                  className="h-8 px-3 rounded-lg text-[10px] uppercase tracking-widest disabled:opacity-50"
                  style={{ background: "linear-gradient(180deg, #FFF3AE, #D4AF37 60%, #6b4d00)", color: "#0a0703" }}>
            {busy ? "Uploading…" : (fileId ? "Replace" : "Upload")}
          </button>
          {fileId && (
            <button type="button" onClick={onClear}
                    data-testid={`${testid}-clear`}
                    className="h-8 px-3 rounded-lg text-[10px] uppercase tracking-widest text-[#F2DC8C]"
                    style={{ border: "1px solid rgba(212,175,55,0.4)" }}>Clear</button>
          )}
        </div>
      </div>
    </FormField>
  );
}
